import React, { useState } from 'react';
import {
  FaUmbrellaBeach, FaShieldAlt, FaChartLine, FaCheckCircle,
  FaChevronDown, FaCoins, FaHeartbeat, FaBalanceScale, FaLandmark
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
const RetirementPlanning = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What is Retirement Planning and why should I start early?",
      a: "Retirement planning involves calculating how much money you will need to maintain your lifestyle after stopping work and building a portfolio to generate that income. Starting early allows you to leverage the power of compounding, meaning smaller monthly savings can grow into a massive corpus over decades with minimal financial strain."
    },
    {
      q: "How much corpus do I need to retire comfortably?",
      a: "As a general rule of thumb, you will need roughly 20 to 25 times your annual living expenses at the time of retirement, factoring in inflation, lifestyle changes, and life expectancy. Our advisors calculate a personalized figure based on your specific expenses and retirement age."
    },
    {
      q: "How do I protect my retirement fund against inflation?",
      a: "Living costs double roughly every 10 to 12 years due to inflation. Relying solely on low-yield traditional savings can erode your purchasing power. A well-structured retirement plan includes an optimal mix of equities for long-term growth and stable income-generating assets to beat inflation."
    },
    {
      q: "What is SWP (Systematic Withdrawal Plan) in retirement?",
      a: "An SWP allows you to withdraw a fixed amount of money from your mutual fund investments at regular intervals (e.g., monthly). It is an efficient post-retirement income tool because it provides steady cash flow while keeping the remaining balance invested to continue growing."
    }
  ];

  return (
    <main className="retirement-planning-page">
      <Helmet>
        {/* Basic SEO */}
        <title>Retirement Planning Services in Hyderabad | Retirement Financial Planning | Lotoswealth</title>

        <meta
          name="description"
          content="Lotoswealth provides expert Retirement Planning Services in Hyderabad, helping individuals build a secure retirement through personalized retirement planning, pension planning, wealth management, investment strategies, tax-efficient retirement solutions, and long-term financial security."
        />

        <meta
          name="keywords"
          content="Retirement Planning Services Hyderabad, Retirement Planning Hyderabad, Retirement Financial Planning Hyderabad, Pension Planning Hyderabad, Retirement Investment Planning Hyderabad, Retirement Wealth Management Hyderabad, Retirement Income Planning Hyderabad, Wealth Management Hyderabad, Financial Planning Hyderabad, Investment Planning Hyderabad, Retirement Advisor Hyderabad, Financial Advisor Hyderabad, Investment Advisor Hyderabad, Pension Funds Hyderabad, Mutual Funds for Retirement Hyderabad, SIP for Retirement Hyderabad, Asset Management Hyderabad, Tax Planning Hyderabad, Financial Independence Hyderabad, Retirement Solutions Hyderabad, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link
          rel="canonical"
          href="https://www.lotoswealth.com/retirement-planning"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta
          property="og:url"
          content="https://www.lotoswealth.com/retirement-planning"
        />
        <meta
          property="og:title"
          content="Retirement Planning Services in Hyderabad | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Plan your retirement with confidence. Lotoswealth offers Retirement Planning Services in Hyderabad, including pension planning, retirement investments, wealth management, and long-term financial security."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Retirement Planning Services in Hyderabad | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Secure your future with Retirement Planning Services in Hyderabad. Personalized retirement investment strategies, pension planning, wealth management, and financial advisory from Lotoswealth."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>
      {/* =========================================
          1. HERO SECTION (With Ticker & Slow Zoom)
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '100vh', minHeight: '800px', overflow: 'hidden' }}>

        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 animate-slow-zoom">
          <img
            src="/images/retirement-planning.jpg"
            alt="Retirement Planning Advisory"
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text (Perfectly Centered) --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Retirement Planning
          </h1>
          <h2 className="animate-slide-up delay-2 fw-bold text-white mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Secure Your Golden Years With Financial Independence
          </h2>
          <p className="hero-description animate-slide-up delay-3 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Welcome to Lotoswealth. Retirement should be the most relaxing and fulfilling chapter of your life—not a time of financial anxiety. Our comprehensive retirement planning solutions ensure you build a powerful corpus during your working years and transition seamlessly into a secure, predictable income stream post-retirement.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-4 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            From combating rising healthcare costs and inflation to optimizing systematic withdrawals (SWP) and annuity structures, we design personalized strategies that guarantee your lifestyle remains uncompromised long after you stop working.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-5 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            It is never too early—or too late—to start planning your future. Let us help you turn decades of hard work into a lifetime of financial security.
          </p>
        </div>

        {/* --- Scrolling Stock Ticker --- */}
        <div className="stock-ticker position-absolute bottom-0 w-100 z-3 bg-dark text-white border-top border-bottom border-secondary py-2" style={{ opacity: 0.9 }}>
          <div className="ticker-track">
            {/* First Set */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>

            {/* Duplicated Set for Infinite Scrolling */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
          </div>
        </div>
      </div>

      {/* =========================================
          2. WHY RETIREMENT PLANNING MATTERS (Soft Teal Theme)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                Why Proactive Retirement Planning is Essential
              </h2>
              <p className="text-muted line-height-lg">
                Increased life expectancies mean that your retirement period could easily span 25 to 30 years. Relying on ad-hoc savings or employer provident funds alone is rarely enough to sustain a comfortable standard of living in the face of steep inflation.
              </p>
              <p className="text-muted line-height-lg">
                A structured retirement plan protects you from outliving your savings. By calculating your future expenses and deploying disciplined accumulation strategies early, you ensure absolute financial autonomy during your golden years.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">Key Benefits of Our Strategy</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Inflation Proofing:</strong> Corpus growth that outpaces cost-of-living spikes.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Guaranteed Cash Flow:</strong> Steady monthly payouts via SWP and annuities.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Healthcare Security:</strong> Dedicated medical emergency buffers and insurance.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Total Peace of Mind:</strong> Freedom from financial dependency on others.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. OUR RETIREMENT SOLUTIONS (Crisp White)
      ========================================= */}
      <section className="py-5 bg-white" style={{ backgroundImage: "url('/images/lotoswealth-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>

        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🏖️ Our Retirement Planning Solutions</h2>
            <p className="text-white">End-to-end accumulation and income distribution strategies.</p>
          </div>

          <div className="row g-4">

            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaChartLine size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Corpus Accumulation Strategy</h5>
                <p className="text-muted small">Design long-term equity and hybrid investment portfolios tailored to your age and target retirement date to maximize wealth compounding.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaCoins size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Post-Retirement Income (SWP)</h5>
                <p className="text-muted small">Structure Systematic Withdrawal Plans (SWPs) and dividend yields to provide a reliable, tax-efficient monthly paycheck during your retirement years.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-3">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaHeartbeat size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Healthcare & Critical Illness Planning</h5>
                <p className="text-muted small">Incorporate comprehensive medical insurance, top-up covers, and dedicated healthcare emergency reserves to protect your retirement savings from unexpected medical bills.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-4">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaLandmark size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Annuity & Pension Optimization</h5>
                <p className="text-muted small">Evaluate and select the best government pension schemes (NPS), senior citizen savings plans, and private annuities to lock in guaranteed baseline income.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-5">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaBalanceScale size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Asset Allocation Rebalancing</h5>
                <p className="text-muted small">Systematically shifting your portfolio from high-growth equities to stable debt instruments as you approach retirement age to safeguard accumulated wealth.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-6">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaShieldAlt size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Legacy & Will Integration</h5>
                <p className="text-muted small">Ensure your retirement assets transition smoothly to your heirs and loved ones according to your explicit wishes through proper estate coordination.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          4. OUR 5-STEP RETIREMENT PROCESS (Dark Slate)
      ========================================= */}
      <section className="py-5 text-white" style={{ backgroundColor: '#0a192f' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔄 Our Retirement Planning Methodology</h2>
            <p className="text-light opacity-75">A clear framework from active accumulation to peaceful enjoyment.</p>
          </div>

          <div className="row g-4">

            <div className="col-md-6 col-lg-4 animate-slide-up delay-1">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 1 – Lifestyle & Expense Mapping</h5>
                <p className="small text-muted mb-0">We project your future monthly living expenses post-retirement, accounting for inflation and lifestyle aspirations.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-2">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 2 – Corpus Calculation</h5>
                <p className="small text-muted mb-0">We determine the exact total target corpus required to fund your post-retirement years up to life expectancy.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-3">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 3 – Savings & SIP Structuring</h5>
                <p className="small text-muted mb-0">Designing a disciplined monthly savings and investment plan to bridge the gap between your current savings and target corpus.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-6 animate-slide-up delay-4">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 4 – Post-Retirement Cash Flow Setup</h5>
                <p className="small text-muted mb-0">Structuring SWPs, debt allocations, and dividend avenues to generate a dependable monthly income once you stop working.</p>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 animate-slide-up delay-5">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 5 – Ongoing Review & Health Checks</h5>
                <p className="small text-muted mb-0">Regularly monitoring your retirement fund performance and adjusting withdrawal rates or allocations as economic conditions evolve.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          5. WHO WE SERVE & WHY CHOOSE US (Soft Blue-Gray)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#eef2f5' }}>
        <div className="container py-4">
          <div className="row g-5">

            {/* Target Audience */}
            <div className="col-lg-5 animate-slide-up delay-1">
              <h3 className="fw-bold mb-4 text-dark">👥 Who Needs Retirement Planning?</h3>
              <p className="text-muted small mb-4">Securing your post-working years is vital for everyone:</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  "Young Professionals Starting Early", "Mid-Career Salaried Employees",
                  "Business Owners & Entrepreneurs", "Pre-Retirees (5-10 Years Away)",
                  "Recent Retirees Seeking Income Structure", "Self-Employed Consultants",
                  "NRIs Planning to Retire in India", "Anyone Seeking Financial Independence"
                ].map((item, idx) => (
                  <span key={idx} className="badge bg-white text-dark border p-2 shadow-sm fs-6">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Why Choose Lotoswealth */}
            <div className="col-lg-7 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <h3 className="fw-bold mb-3 text-dark">🏆 Why Trust Lotoswealth?</h3>
                <p className="text-muted small mb-4">Retirement planning requires precision, patience, and expert foresight. Clients choose us because we offer:</p>
                <div className="row g-3">
                  {[
                    "Accurate Inflation-Adjusted Modeling", "Customized Income Distribution Strategies",
                    "Tax-Efficient Withdrawal Planning", "Dedicated Post-Retirement Support",
                    "Unbiased Product Recommendations", "Comprehensive Healthcare Contingency Design",
                    "Regular Portfolio Health Reviews", "Long-Term Wealth Security Focus"
                  ].map((item, idx) => (
                    <div key={idx} className="col-md-6 d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2 flex-shrink-0" />
                      <span className="small text-muted fw-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          6. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding Retirement & Post-Retirement Planning</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          7. FINAL CTA SECTION (Deep Navy)
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Ready to Secure Your Golden Years?</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            Build a bulletproof retirement plan today and enjoy complete peace of mind tomorrow. Speak with our retirement specialists.
          </p>
          <button className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3">
            Schedule a Retirement Consultation
          </button>
        </div>
      </section>

    </main>
  );
};

export default RetirementPlanning;